<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTenant, AVAILABLE_PERSONAS } from '@/composables/useTenant';
import { useRouter } from 'vue-router';
import { setAuthToken } from '@/services/api';
import { User, LogOut, Settings, Users, Check } from 'lucide-vue-next';
import type { UserSummary } from '@/types/models';

const { t } = useI18n();
const router = useRouter();
const { currentUser, setPersona, logout } = useTenant();
const isOpen = ref(false);

const handleLogout = () => {
  isOpen.value = false;
  logout();
  setAuthToken(null);
  if (typeof window !== 'undefined' && (window as any).Clerk?.signOut) {
    (window as any).Clerk.signOut().catch(() => {});
  }
  router.push('/auth/login');
};

const handleSelectPersona = (persona: UserSummary) => {
  setPersona(persona);
  setAuthToken(`mock_token_${persona.id}`);
  isOpen.value = false;
};

const getRoleBadge = (role: string) => {
  if (role === 'ADMIN' || role === 'ORG_ADMIN' || role === 'TEAM_ADMIN') return t('roles.ADMIN') || '管理者 (ADMIN)';
  if (role === 'COACH' || role === 'HEAD_COACH' || role === 'ASSISTANT_COACH') return t('roles.COACH') || '監督・コーチ (COACH)';
  if (role === 'PLAYER') return t('roles.PLAYER') || '選手 (PLAYER)';
  if (role === 'GUARDIAN' || role === 'PARENT') return t('roles.GUARDIAN') || '保護者 (GUARDIAN)';
  return t('roles.ADMIN') || '管理者';
};
</script>

<template>
  <div class="relative">
    <button
      type="button"
      @click="isOpen = !isOpen"
      class="flex items-center gap-2.5 p-1 pl-2.5 rounded-full bg-slate-950 border border-slate-800 hover:border-slate-700 transition shadow-sm cursor-pointer"
    >
      <span class="text-xs font-semibold text-slate-200 hidden md:inline">
        {{ currentUser.fullName }}
      </span>
      <img
        :src="currentUser.avatarUrl"
        :alt="currentUser.fullName"
        class="w-8 h-8 rounded-full ring-2 ring-sky-500/30 object-cover"
      />
    </button>

    <div
      v-if="isOpen"
      class="absolute right-0 mt-2 w-72 bg-slate-900 border border-slate-700/80 rounded-2xl shadow-2xl py-2 z-50 animate-in fade-in zoom-in-95 duration-100"
      style="background-color: #0f172a;"
    >
      <!-- Current User Profile Info -->
      <div class="px-4 py-2.5 border-b border-slate-800">
        <p class="text-xs font-bold text-slate-100">{{ currentUser.fullName }}</p>
        <p class="text-[11px] text-slate-400 truncate">{{ currentUser.email }}</p>
        <span class="inline-block mt-1.5 px-2 py-0.5 rounded text-[10px] font-bold bg-sky-500/15 text-sky-400 border border-sky-500/30">
          {{ getRoleBadge(currentUser.role) }}
        </span>
      </div>

      <!-- Quick Japanese Persona Switcher -->
      <div class="py-2 px-3 border-b border-slate-800 space-y-1">
        <div class="px-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center justify-between">
          <span>デモアカウント切り替え</span>
          <span class="text-[9px] text-sky-400">RBAC確認</span>
        </div>

        <div class="space-y-0.5 pt-1">
          <button
            v-for="p in AVAILABLE_PERSONAS"
            :key="p.id"
            type="button"
            @click="handleSelectPersona(p)"
            :class="[
              'w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl text-left transition text-xs',
              p.id === currentUser.id
                ? 'bg-sky-500/20 text-white font-bold'
                : 'text-slate-300 hover:bg-slate-800'
            ]"
          >
            <div class="min-w-0 flex items-center gap-2">
              <img :src="p.avatarUrl" class="w-5 h-5 rounded-full object-cover shrink-0" />
              <div class="truncate">
                <span class="truncate">{{ p.fullName.split(' ')[0] }}</span>
                <span class="text-[10px] text-slate-400 ml-1">({{ p.role }})</span>
              </div>
            </div>
            <Check v-if="p.id === currentUser.id" class="w-3.5 h-3.5 text-sky-400 shrink-0" />
          </button>
        </div>
      </div>

      <!-- Settings Link -->
      <div class="py-1">
        <router-link
          to="/dashboard/settings"
          @click="isOpen = false"
          class="flex items-center gap-2.5 px-4 py-2 text-xs font-medium text-slate-300 hover:text-white hover:bg-slate-800 transition"
        >
          <Settings class="w-4 h-4 text-slate-400" />
          <span>{{ t('nav.settings') }}</span>
        </router-link>
      </div>

      <!-- Sign Out -->
      <div class="pt-1 mt-1 border-t border-slate-800">
        <button
          type="button"
          @click="handleLogout"
          class="w-full flex items-center gap-2.5 px-4 py-2 text-xs font-medium text-rose-400 hover:bg-rose-500/10 transition text-left"
        >
          <LogOut class="w-4 h-4" />
          <span>{{ t('nav.signOut') }}</span>
        </button>
      </div>
    </div>

    <div v-if="isOpen" class="fixed inset-0 z-40" @click="isOpen = false" />
  </div>
</template>
